import React from "react";
import ClickOutHandler from 'react-onclickout';
import { Motion, spring } from 'react-motion';
import { Manager, Target, Popper } from "react-popper";

import { noop, keycodeFromEvent } from 'shared/helpers';



/*
 * TargetWrapper
 *
 * Wraps "target" children to facilitate with certain event handling.
 * Needs to work inside Manager, not exported.
 */
class TargetWrapper extends React.Component {

  constructor(props) {
    super(props);

    this.handleKeydown = this.handleKeydown.bind(this);
  }

  componentDidMount() {
    this.target.addEventListener("keydown", this.handleKeydown, false);
    this.target.addEventListener("click", this.props.togglePopover, false);
  }

  componentWillUnmount() {
    this.target.removeEventListener("keydown", this.handleKeydown, false);
    this.target.removeEventListener("click", this.props.togglePopover, false);
  }

  handleKeydown = (e) => {
    const { code, kc } = keycodeFromEvent(e);
    const isEscape = code === kc.codes.esc;
    if (isEscape) {
      return this.props.closePopover();
    }
  }

  render() {
    const { targetProps } = this.props;

    return (
      <Target
        innerRef={ref => this.target = ref}
        {...targetProps}
      >
        {this.props.children}
      </Target>
    );
  }
}



/*
 * PopperWrapper
 *
 * Wraps "popper" children to facilitate with certain event handling
 * and style adjustment etc. Needs to work inside Manager, not exported.
 */
class PopperWrapper extends React.Component {
  constructor(props) {
    super(props);

    // Don't name this isMounted, because it's a deprecated
    // React instance method and will throw an error.
    // https://github.com/azmenak/react-stripe-checkout/issues/83
    this.mounted = false;

    this.handleClickInside = this.handleClickInside.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    this.props.onOpen();
    this.popper.addEventListener("click", this.handleClickInside, false);
  }

  componentWillUnmount() {
    this.mounted = false;
    this.props.onClose();
    this.popper.removeEventListener("click", this.handleClickInside, false);
  }

  handleClickInside(e) {
    const { isInteractive, closePopover } = this.props;

    if (!isInteractive) {
      // Hmm, calling closePopover() directly seems to unmount
      // popper element and prevent it from detecting a click event.
      // setTimeout seems to do the trick..?
      return setTimeout(() => this.mounted && closePopover(), 0);
    }
  }

  updatePopperStyle(popperProps) {
    const { popperStyle } = this.props;
    popperProps.style = {
      ...popperProps.style,
      minWidth: '220px',
      ...popperStyle,
    };

    if (popperProps['data-placement']) {
      // Popover distance from target.
      const { awayFromTarget } = this.props;

      // Split for variations like 'top-end'..
      const [ position ] = popperProps['data-placement'].split('-');
      const isVertical = ['top', 'bottom'].includes(position);
      const isPositive = ['bottom', 'right'].includes(position);

      // Popper.js deals with 'top' and 'left'.
      const targetStyle = isVertical ? 'top' : 'left';

      // Multiplier for direction
      const direction = isPositive ? 1 : -1;

      const currentVal = popperProps.style[targetStyle] || 0;
      const updatedVal = currentVal + (awayFromTarget * direction);
      popperProps.style[targetStyle] = updatedVal;
    }

    return popperProps;
  }

  render() {
    const {
      className,
      placement,
      modifiers,
      closePopover,
      children
    } = this.props;

    return (
      <Popper
        placement={placement}
        modifiers={modifiers}
        innerRef={ref => this.popper = ref}
      >
        {({ popperProps }) => {
          popperProps = this.updatePopperStyle(popperProps);

          if (className) {
            popperProps.className += ` ${className}`;
          }

          return (
            <ClickOutHandler onClickOut={closePopover}>
              {typeof children === 'function' ? (
                children(popperProps)
              ) : (
                <div {...popperProps}>{children}</div>
              )}
            </ClickOutHandler>
          );

        }}
      </Popper>
    );
  }
}



/*
 * Popover
 *
 * Main popover component where two children are expected:
 * 1) Target
 * 2) Popper
 *
 * Popper component can be a standalone React component or
 * called with popWithMotion(element) to give react-motion based
 * animation when appearing.
 *
 * Referenced heavily:
 * https://github.com/vaheqelyan/react-awesome-popover
 */
class Popover extends React.Component {
  constructor(props) {
    super(props);

    const { isOpen = false } = props;

    this.state = { isOpen };

    this.openPopover = this.openPopover.bind(this);
    this.closePopover = this.closePopover.bind(this);
    this.togglePopover = this.togglePopover.bind(this);
  }

  componentWillReceiveProps({ isOpen }) {
    if (isOpen && !this.state.isOpen) {
      return this.setState({ isOpen: true });
    }

    if (!isOpen && this.state.isOpen) {
      return this.setState({ isOpen: false });
    }
  }

  openPopover() {
    this.setState({ isOpen: true });
  }

  closePopover() {
    this.setState({ isOpen: false });
  }

  togglePopover() {
    this.setState({ isOpen: !this.state.isOpen });
  }

  render() {
    const {
      // Target
      trigger = "click",  // XXX: For now only click haha
      targetProps = {},

      // Popper
      className,
      onOpen = noop,
      onClose = noop,
      placement = 'auto',
      modifiers = {},
      awayFromTarget = 10, // px
      isInteractive = false,
      // arrow,  // XXX: No arrow for now lol
      popperStyle = {},
      children,
    } = this.props;

    const [ target, popper ] = children;

    return (
      <Manager tag={false} style={{ display: "inline-flex" }}>
        <TargetWrapper
          targetProps={targetProps}
          trigger={trigger}
          closePopover={this.closePopover}
          openPopover={this.openPopover}
          togglePopover={this.togglePopover}
        >
          {target}
        </TargetWrapper>

        {this.state.isOpen ? (
          <PopperWrapper
            popperStyle={popperStyle}
            className={className}
            onOpen={onOpen}
            onClose={onClose}
            placement={placement}
            modifiers={modifiers}
            awayFromTarget={awayFromTarget}
            isInteractive={isInteractive}
            closePopover={this.closePopover}
          >
            {popper}
          </PopperWrapper>
        ) : null}
      </Manager>
    );
  }
}


/*
 * popWithMotion
 *
 * Helper function to wrap a given component with Motion in order to
 * give some natural animation and smoothness when they appear.
 */
const springParams = { stiffness: 250, damping: 13 };
const popWithMotion = (children, opts = {}) => (popperProps) => {
  const { fromPosY = 10, fromScale = 0.98 } = opts;

  return (
    <Motion
      defaultStyle={{ o: 0.94, y: fromPosY, s: fromScale }}
      style={{
        o: spring(1),
        y: spring(0, springParams),
        s: spring(1, springParams),
      }}
    >
      {({o, y, s}) => {
        const motionStyle = {
          opacity: o,
          top: (popperProps.style.top || 0) + y,
          transform: [
            popperProps.style.transform,
            `scale(${s})`,
          ].filter(t => t).join(' '),
        };

        return (
          <div
            {...popperProps}
            style={{ ...popperProps.style, ...motionStyle }}
          >
            {children}
          </div>
        );
      }}
    </Motion>
  );
};


export {
  Popover,
  popWithMotion,
}



// WEBPACK FOOTER //
// ./src/components/elems/Popover.js